<template>
  <BaseModal
    :is-open="isOpen"
    :title="data?.title || 'Invalid Move'"
    max-width="550px"
    @close="handleClose"
  >
    <!-- Error Message -->
    <div class="rule-violation-container">
      <div class="error-icon">
        <span class="material-symbols-outlined">error</span>
      </div>

      <p class="error-message">{{ data?.message || 'This action is not allowed.' }}</p>

      <!-- Tips Section -->
      <div v-if="data?.tips && data.tips.length > 0" class="tips-section">
        <h4 class="tips-title">
          <span class="material-symbols-outlined">lightbulb</span>
          <span>Tips:</span>
        </h4>
        <ul class="tips-list">
          <li v-for="(tip, index) in data.tips" :key="index" class="tip-item">
            {{ tip }}
          </li>
        </ul>
      </div>

      <!-- Common Rules Reference (if no specific tips) -->
      <div v-else class="common-rules">
        <h4 class="rules-title">Common Rules:</h4>
        <ul class="rules-list">
          <li>Melds require at least 2 natural cards of the same rank</li>
          <li>Natural cards must outnumber wild cards in a meld</li>
          <li>Maximum 3 wild cards per meld (before canasta)</li>
          <li>Red 3s and Black 3s cannot be melded</li>
          <li>Opening meld must meet minimum point requirement</li>
        </ul>
      </div>

      <!-- Action Button -->
      <button @click="handleClose" class="btn-understand">
        <span class="material-symbols-outlined">check_circle</span>
        <span>I Understand</span>
      </button>
    </div>
  </BaseModal>
</template>

<script setup lang="ts">
import BaseModal from './BaseModal.vue'

/**
 * RuleViolationModal Component
 *
 * Displays rule violation errors with helpful tips and guidance.
 * Helps players understand game rules and why their action was invalid.
 *
 * Props:
 * - title: Error title (default: "Invalid Move")
 * - message: Specific error message
 * - tips: Array of helpful tips to guide the player
 */

interface RuleViolationData {
  title?: string
  message?: string
  tips?: string[]
}

interface Props {
  isOpen: boolean
  data: RuleViolationData | null
}

const props = withDefaults(defineProps<Props>(), {
  isOpen: false,
  data: null,
})

const emit = defineEmits<{
  close: []
}>()

/**
 * Handle close
 */
function handleClose(): void {
  emit('close')
}
</script>

<style scoped>
.rule-violation-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
  text-align: center;
}

/* Error Icon */
.error-icon {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(239, 68, 68, 0.2), rgba(220, 38, 38, 0.2));
  display: flex;
  align-items: center;
  justify-content: center;
  animation: pulse-error 2s ease-in-out infinite;
}

.error-icon .material-symbols-outlined {
  font-size: 3rem;
  color: #f87171;
}

@keyframes pulse-error {
  0%,
  100% {
    box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.4);
  }
  50% {
    box-shadow: 0 0 0 20px rgba(239, 68, 68, 0);
  }
}

/* Error Message */
.error-message {
  font-size: 1.125rem;
  color: rgba(255, 255, 255, 0.95);
  line-height: 1.6;
  margin: 0;
  max-width: 450px;
}

/* Tips Section */
.tips-section {
  width: 100%;
  background: rgba(232, 197, 71, 0.05);
  border: 1px solid rgba(232, 197, 71, 0.2);
  border-radius: 12px;
  padding: 1.5rem;
  text-align: left;
}

.tips-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1rem;
  font-weight: 600;
  color: #e8c547;
  margin: 0 0 1rem;
}

.tips-title .material-symbols-outlined {
  font-size: 1.25rem;
}

.tips-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.tip-item {
  padding-left: 1.5rem;
  position: relative;
  font-size: 0.9375rem;
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.5;
}

.tip-item::before {
  content: '→';
  position: absolute;
  left: 0;
  color: #e8c547;
  font-weight: 600;
}

/* Common Rules */
.common-rules {
  width: 100%;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  padding: 1.5rem;
  text-align: left;
}

.rules-title {
  font-size: 1rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  margin: 0 0 1rem;
}

.rules-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
}

.rules-list li {
  padding-left: 1.5rem;
  position: relative;
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.5;
}

.rules-list li::before {
  content: '•';
  position: absolute;
  left: 0.5rem;
  color: rgba(232, 197, 71, 0.6);
  font-weight: 600;
}

/* Action Button */
.btn-understand {
  padding: 0.875rem 2rem;
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  border: none;
  background: linear-gradient(135deg, #e8c547 0%, #d4af37 100%);
  color: #0d1f2d;
  box-shadow: 0 4px 12px rgba(232, 197, 71, 0.3);
  margin-top: 0.5rem;
}

.btn-understand:hover {
  background: linear-gradient(135deg, #f0d060 0%, #e0bf47 100%);
  box-shadow: 0 6px 16px rgba(232, 197, 71, 0.4);
  transform: translateY(-2px);
}

.btn-understand:active {
  transform: translateY(0);
  box-shadow: 0 2px 8px rgba(232, 197, 71, 0.3);
}

.btn-understand .material-symbols-outlined {
  font-size: 1.25rem;
}

/* Responsive */
@media (max-width: 640px) {
  .error-icon {
    width: 64px;
    height: 64px;
  }

  .error-icon .material-symbols-outlined {
    font-size: 2.5rem;
  }

  .error-message {
    font-size: 1rem;
  }

  .tips-section,
  .common-rules {
    padding: 1.25rem;
  }
}
</style>
